import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Token-Refresh: max. 1x pro Anfrage, nie für Auth-Endpunkte selbst, parallele 401 teilen sich einen Refresh
  useEffect(() => {
    let refreshPromise = null;
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const url = originalRequest.url || '';
        const isAuthCall = /\/auth\/(refresh|login|logout|register)\b/.test(url);
        if (error.response?.status !== 401 || originalRequest._retry || isAuthCall) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          refreshPromise = refreshPromise || axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { setTimeout(() => { refreshPromise = null; }, 0); });
          const { data } = await refreshPromise;
          if (data?.access_token) {
            localStorage.setItem('token', data.access_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
            originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${data.access_token}` };
          }
          return axios(originalRequest);
        } catch (refreshError) {
          clearSession();
          return Promise.reject(error);
        }
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [clearSession]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, {
      withCredentials: true  // Für Cookie-Support
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData, {
      withCredentials: true
    });
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = useCallback(async () => {
    try {
      // Server-seitiger Logout (löscht Cookies)
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn('Server logout failed:', error);
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const updateProfile = async (updates) => {
    await axios.put(`${API_URL}/users/profile`, updates);
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
